/**
 * AuthService — CRM session management (Microsoft SSO or legacy password login).
 *
 * Profile metadata may live in sessionStorage (no JWTs).
 * Access token: ApiClient memory only.
 * Refresh token: httpOnly cookie set by the API.
 */

import { IdentityApiClient } from './IdentityApiClient.js';
import { ApiClient } from './ApiClient.js';
import { AzureAdAuthService } from './AzureAdAuthService.js';
import { parseAuthSession } from '../../schemas/auth.js';

const SESSION_KEY = 'quickcrm_session';
const LOCAL_KEY = 'quickcrm_session_local';
const BC_NAME = 'quickcrm_auth_sync';

function stripTokensFromSession(session) {
    if (!session || typeof session !== 'object') return session;
    const {
        identityToken: _a,
        refreshToken: _b,
        accessToken: _c,
        access_token: _d,
        refresh_token: _e,
        ...safe
    } = session;
    return safe;
}

function writeSession(value) {
    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        const safe = stripTokensFromSession(parsed);
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(safe));
    } catch {}
    try { localStorage.removeItem(LOCAL_KEY); } catch {}
}

function removeBoth() {
    ApiClient.clearAccessToken();
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
    try { localStorage.removeItem(LOCAL_KEY); } catch {}
}

try {
    if (typeof window !== 'undefined') {
        try { localStorage.removeItem(LOCAL_KEY); } catch {}
        // Migrate: drop any tokens left in an older session blob
        try {
            const raw = sessionStorage.getItem(SESSION_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed?.identityToken || parsed?.refreshToken) {
                    writeSession(parsed);
                }
            }
        } catch {}

        if ('BroadcastChannel' in window) {
            try {
                const bc = new BroadcastChannel(BC_NAME);
                bc.onmessage = async (ev) => {
                    const { type, payload } = ev.data || {};
                    if (type === 'auth-sync' && payload) {
                        try {
                            writeSession(payload);
                            await ApiClient.tryRefreshToken();
                            window.dispatchEvent(new CustomEvent('authChanged', {
                                detail: { user: AuthService.getSession() },
                            }));
                        } catch {}
                    } else if (type === 'auth-logout') {
                        removeBoth();
                        window.dispatchEvent(new CustomEvent('authChanged', { detail: { user: null } }));
                    } else if (type === 'auth-request') {
                        const cur = sessionStorage.getItem(SESSION_KEY);
                        if (cur) bc.postMessage({ type: 'auth-sync', payload: cur });
                    }
                };
                const existing = sessionStorage.getItem(SESSION_KEY);
                if (!existing) {
                    bc.postMessage({ type: 'auth-request' });
                }
            } catch {}
        }
    }
} catch {}

export class AuthService {
    static getSession() {
        try {
            const raw = sessionStorage.getItem(SESSION_KEY);
            if (!raw) return null;
            return parseAuthSession(stripTokensFromSession(JSON.parse(raw)));
        } catch {
            return null;
        }
    }

    static isAuthenticated() {
        const s = AuthService.getSession();
        return !!(s && s.userId && s.status === 'active' && ApiClient.getToken());
    }

    static hasProfile() {
        const s = AuthService.getSession();
        return !!(s && s.userId && s.status === 'active');
    }

    /**
     * Restore access token from httpOnly refresh cookie and rebuild profile if needed.
     * @returns {Promise<object|null>}
     */
    static async restoreSession() {
        const refreshed = await ApiClient.tryRefreshToken();
        if (!refreshed) {
            removeBoth();
            return null;
        }
        const token = ApiClient.getToken();
        if (!token) {
            removeBoth();
            return null;
        }
        try {
            return await AuthService.establishSessionFromAccessToken(token, '', null);
        } catch {
            removeBoth();
            return null;
        }
    }

    static async establishSessionFromAccessToken(accessToken, fallbackEmail = '', authProvider = 'password') {
        if (!accessToken) throw new Error('Login failed: access token missing.');
        ApiClient.setAccessToken(accessToken);

        const meRes = await fetch(`${ApiClient.getBaseUrl()}/api/v1/auth/me`, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            credentials: 'include',
        });
        const meJson = await meRes.json().catch(() => ({}));

        if (!meRes.ok || meJson?.error) {
            ApiClient.clearAccessToken();
            throw new Error(meJson?.error?.message || 'Failed to fetch profile.');
        }

        const meUser = meJson?.data || {};
        const prev = AuthService.getSession() || {};
        const avatarUrl = meUser.avatar_url || (meUser.has_avatar ? `/api/v1/users/${meUser.id}/avatar` : (prev.avatarUrl || ''));
        const session = stripTokensFromSession({
            userId: meUser.id || meUser.userId || null,
            name: meUser.full_name || meUser.fullName || meUser.name || '',
            email: meUser.email || fallbackEmail,
            role: meUser.role || '',
            scopes: meUser.scopes && typeof meUser.scopes === 'object' ? meUser.scopes : {},
            department: prev.department || '',
            status: 'active',
            avatarUrl,
            hasAvatar: !!meUser.has_avatar,
            avatarUpdatedAt: meUser.avatar_updated_at || null,
            loggedInAt: prev.loggedInAt || new Date().toISOString(),
            authProvider: authProvider || prev.authProvider || 'password',
        });

        const serialized = JSON.stringify(session);
        writeSession(serialized);
        try {
            if ('BroadcastChannel' in window) {
                const bc = new BroadcastChannel(BC_NAME);
                bc.postMessage({ type: 'auth-sync', payload: serialized });
                bc.close();
            }
        } catch {}
        window.dispatchEvent(new CustomEvent('authChanged', { detail: { user: session } }));
        return session;
    }

    /** @deprecated use establishSessionFromAccessToken — kept for call-site compatibility */
    static async establishSessionFromTokens(tokens, fallbackEmail = '', authProvider = 'password') {
        const token = tokens?.access_token || tokens?.accessToken || null;
        return AuthService.establishSessionFromAccessToken(token, fallbackEmail, authProvider);
    }

    static async completeMicrosoftRedirect() {
        if (!AzureAdAuthService.isConfigured()) return null;
        if (!AzureAdAuthService.isRedirectReturn()) return null;

        const result = await AzureAdAuthService.handleRedirectPromise();
        if (!result?.idToken) return null;

        const loginRes = await fetch(`${ApiClient.getBaseUrl()}/api/v1/auth/microsoft`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ id_token: result.idToken }),
        });
        const loginJson = await loginRes.json().catch(() => ({}));

        if (!loginRes.ok || loginJson?.error || !loginJson?.data) {
            const msg = loginJson?.error?.message || 'Microsoft sign-in failed.';
            throw new Error(msg);
        }

        const email = result.account?.username || result.account?.idTokenClaims?.preferred_username || '';
        return AuthService.establishSessionFromAccessToken(loginJson.data.access_token, email, 'microsoft');
    }

    static async loginWithMicrosoft() {
        const configError = AzureAdAuthService.getConfigError();
        if (configError) throw new Error(configError);
        await AzureAdAuthService.loginRedirect();
    }

    static async login(email, password) {
        const formBody = new URLSearchParams();
        formBody.set('username', email);
        formBody.set('password', password);

        const loginRes = await fetch(`${ApiClient.getBaseUrl()}/api/v1/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Accept: 'application/json',
            },
            credentials: 'include',
            body: formBody.toString(),
        });

        const loginJson = await loginRes.json().catch(() => ({}));
        if (!loginRes.ok || loginJson?.error || !loginJson?.data) {
            const msg = loginJson?.error?.message || loginJson?.message || 'Login failed.';
            throw new Error(msg);
        }

        return AuthService.establishSessionFromAccessToken(loginJson.data.access_token, email, 'password');
    }

    static async register(email, password) {
        return IdentityApiClient.register(email, password);
    }

    static async resetPassword(email, password) {
        return IdentityApiClient.resetPassword(email, password);
    }

    static async logout() {
        const session = AuthService.getSession();
        const provider = session?.authProvider;
        await ApiClient.logoutServer();
        removeBoth();
        try {
            if ('BroadcastChannel' in window) {
                const bc = new BroadcastChannel(BC_NAME);
                bc.postMessage({ type: 'auth-logout' });
                bc.close();
            }
        } catch {}
        window.dispatchEvent(new CustomEvent('authChanged', { detail: { user: null } }));

        if (provider === 'microsoft') {
            await AzureAdAuthService.logoutRedirect();
        }
    }

    static refreshSession(user) {
        const session = AuthService.getSession();
        if (!session || !user) return session;
        if (String(session.userId) !== String(user.id)) return session;
        const updated = stripTokensFromSession({
            ...session,
            name: user.name || user.fullName || user.full_name || session.name || '',
            email: user.email || session.email || '',
            role: user.role || session.role || '',
            department: user.department || '',
            status: user.status || session.status || 'active',
            avatarUrl: user.avatarUrl || user.avatar_url || session.avatarUrl || '',
            hasAvatar: user.hasAvatar ?? user.has_avatar ?? session.hasAvatar ?? false,
            avatarUpdatedAt: user.avatarUpdatedAt || user.avatar_updated_at || session.avatarUpdatedAt || null,
        });
        const serialized = JSON.stringify(updated);
        writeSession(serialized);
        window.dispatchEvent(new CustomEvent('authChanged', { detail: { user: updated } }));
        return updated;
    }

    static async getCurrentUser() {
        return AuthService.getSession();
    }

    static async refreshScopes() {
        const session = AuthService.getSession();
        const token = ApiClient.getToken();
        if (!session || !token) return session;

        try {
            const meRes = await fetch(`${ApiClient.getBaseUrl()}/api/v1/auth/me`, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                credentials: 'include',
            });
            const meJson = await meRes.json().catch(() => ({}));
            if (!meRes.ok || meJson?.error) return session;

            const scopes = meJson?.data?.scopes;
            if (!scopes || typeof scopes !== 'object') return session;

            const updated = stripTokensFromSession({ ...session, scopes });
            writeSession(JSON.stringify(updated));
            return updated;
        } catch {
            return session;
        }
    }
}

export const auth = AuthService;
